// ============================================================================
// CHANGO V5.0 — Ops Dashboard (admin-only shell)
// ============================================================================
// Minimal post-login landing for the platform-admin role. Summarises the
// admin's identity and points to the (still unbuilt) control surfaces that
// ship in D2.5 / D3 / D4:
//   - workspace review queue (approve pending_review → active)
//   - feature-flag editor
//   - per-workspace SKU pricing override
//   - audit log viewer
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';

// ---- 系统健康 tile --------------------------------------------------------
// Polls /api/ops/health/libreoffice + /api/ops/health/oss and renders each
// as a colour badge. Both endpoints return 200 with `body.ok` so the tile
// treats HTTP 500s as "probe failed to run" (a different class of failure
// from "dependency unhealthy"). Poll interval is 30s — probes are cheap
// (~ms round-trip) and the tile re-renders only when state actually flips.
// ============================================================================

const HEALTH_POLL_MS = 30_000;

function HealthBadge({ ok, label }) {
  const cls = ok
    ? 'bg-emerald-700 text-emerald-100'
    : ok === false
      ? 'bg-rose-700 text-rose-100'
      : 'bg-slate-700 text-slate-300';
  const txt = ok ? '正常' : ok === false ? '异常' : '加载中';
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`} title={label}>
      {txt}
    </span>
  );
}

function useHealthProbe(path) {
  const [state, setState] = useState({ ok: null, data: null, error: null });
  const inFlight = useRef(false);

  const tick = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await api.get(path);
      setState({ ok: Boolean(data?.ok), data, error: null });
    } catch (e) {
      // A probe endpoint that itself errors is rendered as "unknown" —
      // distinct from the dependency being unhealthy.
      setState({
        ok: null,
        data: null,
        error: e instanceof HttpError ? `${e.code || 'ERROR'}: ${e.message}` : (e?.message || 'unknown error'),
      });
    } finally {
      inFlight.current = false;
    }
  }, [path]);

  useEffect(() => {
    tick();
    const id = setInterval(tick, HEALTH_POLL_MS);
    return () => clearInterval(id);
  }, [tick]);

  return { ...state, refresh: tick };
}

function SystemHealthTile() {
  const lo = useHealthProbe('/api/ops/health/libreoffice');
  const oss = useHealthProbe('/api/ops/health/oss');

  return (
    <section className="mt-8 rounded-lg border border-slate-700 bg-slate-800/60 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">系统健康</h2>
        <button
          type="button"
          onClick={() => { lo.refresh(); oss.refresh(); }}
          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700"
        >
          刷新
        </button>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="flex items-center justify-between">
            <dt className="text-sm font-medium">LibreOffice</dt>
            <HealthBadge ok={lo.ok} label="libreoffice probe" />
          </div>
          <dd className="mt-1 text-xs text-slate-400">
            {lo.data?.ok && (
              <span className="font-mono">{lo.data.version || lo.data.bin}</span>
            )}
            {lo.data?.ok === false && (
              <span className="text-rose-300">
                {lo.data.code || 'UNKNOWN'}
                {lo.data.message ? ` — ${lo.data.message}` : ''}
              </span>
            )}
            {!lo.data && lo.error && (
              <span className="text-amber-300">{lo.error}</span>
            )}
            {!lo.data && !lo.error && <span>加载中…</span>}
          </dd>
        </div>

        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="flex items-center justify-between">
            <dt className="text-sm font-medium">
              对象存储 <span className="text-xs text-slate-400">({oss.data?.backend || '…'})</span>
            </dt>
            <HealthBadge ok={oss.ok} label="oss probe" />
          </div>
          <dd className="mt-1 text-xs text-slate-400">
            {oss.data?.ok && oss.data.backend === 'aliyun' && (
              <span className="font-mono">bucket={oss.data.bucket} · region={oss.data.region}</span>
            )}
            {oss.data?.ok && oss.data.backend === 'local' && (
              <span className="font-mono">{oss.data.dir}</span>
            )}
            {oss.data?.ok === false && (
              <span className="text-rose-300">
                {oss.data.code || 'UNKNOWN'}
                {oss.data.message ? ` — ${oss.data.message}` : ''}
              </span>
            )}
            {!oss.data && oss.error && (
              <span className="text-amber-300">{oss.error}</span>
            )}
            {!oss.data && !oss.error && <span>加载中…</span>}
          </dd>
        </div>
      </dl>

      <p className="mt-3 text-xs text-slate-500">
        每 30 秒自动刷新 · Grafana 同时会拉这两个探针。
      </p>
    </section>
  );
}

// Live ops surfaces — all shipped & routed. Rendered as a navigable list
// so admins don't have to guess the URL of each console.
const LIVE = [
  { path: '/ops/customers',         label: '客户 / 用户管理',  desc: '全部工作区 · 负责人 · 成员 · 套餐到期 · 赠送会员' },
  { path: '/ops/review-requests',   label: '审批收件箱',      desc: '双人复核队列 (§20 #4)' },
  { path: '/ops/workspaces/audit-queue', label: '工作区审核队列', desc: '试用企业资料复核 · 通过 / 冻结' },
  { path: '/ops/sku-pricing',       label: 'SKU 定价',         desc: '平台产品定价 (两人制发布)' },
  { path: '/ops/admin-seats',       label: 'admin 席位',       desc: '授予 / 撤销 admin 角色' },
  { path: '/ops/mail-inbound',      label: '邮件回复收件箱',   desc: 'D6.6 — 回复自动发布审阅' },
  { path: '/ops/legal-agreements',  label: '法律协议',         desc: 'TOS / 隐私政策 / 计费条款 上传 (§11.10)' },
  { path: '/ops/system-config',     label: '系统配置',         desc: '计费模式 · 试用天数 · 软归档阈值' },
  { path: '/ops/archived-workspaces', label: '已归档工作区',     desc: '软归档台账 · 归档预览 · 两人制重新激活' },
  { path: '/ops/audit-log',         label: '操作审计日志',     desc: 'ops_action_log / ops_audit_log 只读查看' },
  { path: '/ops/cms/pages',         label: 'CMS · 页面',       desc: '营销站页面编辑 · 8 种块类型' },
  { path: '/ops/cms/notices',       label: 'CMS · 全站公告',   desc: '站内横幅 · 支持时间窗口调度' },
];

const UPCOMING = [
  { path: '/ops/feature-flags',  label: '功能开关编辑器', when: 'D3' },
  { path: '/ops/sku-overrides',  label: '按工作区 SKU 价格覆盖', when: 'D3' },
];

export default function OpsDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate('/ops/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-700 bg-slate-800/80">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">Ops Console</h1>
            <p className="text-xs text-slate-400">CHANGO V5.0 · 平台管理员视图</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-slate-300">{user?.email}</span>
            <span className="rounded bg-chango-500 px-2 py-1 text-xs font-mono text-white">
              {user?.role}
            </span>
            <button type="button" onClick={onLogout} className="rounded border border-slate-600 px-3 py-1 text-xs text-slate-200 hover:bg-slate-700">
              登出
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <section className="rounded-lg border border-slate-700 bg-slate-800/60 p-6">
          <h2 className="mb-4 text-base font-semibold">管理员身份</h2>
          <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            <div>
              <dt className="text-xs uppercase text-slate-400">用户 ID</dt>
              <dd className="font-mono">{user?.id}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-400">邮箱</dt>
              <dd>{user?.email}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-400">角色</dt>
              <dd>{user?.role}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase text-slate-400">2FA</dt>
              <dd>{user?.two_fa_enabled ? '已启用' : '未启用（异常）'}</dd>
            </div>
          </dl>
        </section>

        <SystemHealthTile />

        <section className="mt-8">
          <h2 className="mb-4 text-base font-semibold">可用控制台</h2>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {LIVE.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="block rounded-md border border-slate-700 bg-slate-800/40 p-4 hover:border-chango-500 hover:bg-slate-800/80 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{item.label}</span>
                    <span className="rounded bg-emerald-700 px-2 py-0.5 text-xs text-emerald-100">已上线</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-400">{item.desc}</div>
                  <div className="mt-1 font-mono text-xs text-slate-500">{item.path}</div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="mb-4 text-base font-semibold">即将开放</h2>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {UPCOMING.map((item) => (
              <li key={item.path} className="rounded-md border border-slate-700 bg-slate-800/40 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{item.label}</span>
                  <span className="rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-300">
                    {item.when}
                  </span>
                </div>
                <div className="mt-1 font-mono text-xs text-slate-500">{item.path}</div>
              </li>
            ))}
          </ul>
        </section>

        <p className="mt-10 text-center text-xs text-slate-500">
          管理员操作会写入 ops_action_log / ops_audit_log；请谨慎操作。
        </p>
      </main>
    </div>
  );
}
